import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import './Insights.css';
import { Helmet } from 'react-helmet';

const NmtcClimateJustice = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    // Add fade-in effect when component mounts
    setIsLoaded(true);
    
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  // Define simple paragraph style
  const simpleParagraphStyle = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333',
    margin: '0 0 1.2em 0',
    fontWeight: 'normal'
  };

  // Title style with enhanced visibility
  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: '1.5rem',
    marginTop: '0.5rem',
    color: '#1B4620',
    textAlign: 'center',
    position: 'relative',
    padding: '0.5rem 0',
    borderBottom: '3px solid #F7941D',
    display: 'inline-block'
  };

  // Heading styles
  const headingStyle = {
    fontSize: '1.6rem',
    fontWeight: '600',
    marginTop: '1.8rem',
    marginBottom: '1rem',
    color: '#1B4620'
  };

  const subheadingStyle = {
    fontSize: '1.3rem',
    fontWeight: '600',
    marginTop: '1.5rem',
    marginBottom: '0.8rem',
    color: '#1B4620'
  };

  return (
    <>
      <Helmet>
        <title>The Role of NMTC in Climate Justice | Clarity Impact Finance</title>
        <meta name="description" content="Explore how New Markets Tax Credits can be leveraged to promote climate justice through green building and sustainable technology in underserved communities." />
        <meta property="og:title" content="The Critical Role of NMTC in Promoting Climate Justice" />
        <meta property="og:description" content="How New Markets Tax Credits support climate justice initiatives through green building and technology." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://clarityimpactfinance.com/nmtc-climate-justice" />
        <meta property="og:image" content="https://res.cloudinary.com/dxenrdunh/image/upload/v1743291175/iStock-1472610843_wxnesf.jpg" />
      </Helmet>
      <Navbar />
      <div className={`article-container ${isLoaded ? 'fade-in' : ''}`} style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div className="article-header" style={{ textAlign: 'center', marginBottom: '3rem', backgroundColor: '#f8f9fa', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
          <h1 style={titleStyle}>The Critical Role of NMTC in Promoting Climate Justice through Green Building and Technology</h1>
          <div className="article-meta" style={{ textAlign: 'center' }}>
            <p className="article-author" style={{ margin: '1rem 0 0.5rem', fontSize: '1.1rem' }}>By Amir A., Founder at Clarity Impact Finance</p>
            <p className="article-date" style={{ margin: '0.5rem 0', color: '#666' }}>Published May 15, 2024</p>
          </div>
        </div>
        
        <div className="article-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="article-body" style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
            <div className="article-summary" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
              <p style={simpleParagraphStyle}>The New Markets Tax Credit (NMTC) federal program, designed to stimulate economic development in historically excluded communities, also holds potential as a powerful ally in the fight against climate change. By directing capital toward sustainable development projects, NMTC can help address the disproportionate impact of environmental degradation on low-income communities, aligning with broader climate justice goals.</p>
            </div>
            
            <p style={simpleParagraphStyle}>By funding projects, such as the construction of energy-efficient buildings or the development of renewable energy resources, NMTC supports not only economic growth but also contributes to environmental sustainability. These projects help reduce greenhouse gas emissions. Additionally, they can decrease the urban heat-island effect, thereby mitigating the impact of climate change on vulnerable populations.</p>
            
            <p style={simpleParagraphStyle}>For instance, NMTC can support projects that incorporate green roofs, urban forests and improved water-management systems that help communities withstand extreme weather events like heavy rainfall and heatwaves.</p>
            
            <div className="article-callout" style={{ borderLeft: '4px solid #1B4620', padding: '0 0 0 1rem', margin: '1.5rem 0' }}>
              <p style={simpleParagraphStyle}>Climate justice recognizes that those who have contributed least to climate change often suffer its worst effects. NMTC provides a mechanism to direct investments toward these communities, helping to correct this imbalance.</p>
            </div>
            
            <h2 style={headingStyle}>Addressing Historical Disparities</h2>
            
            <p style={simpleParagraphStyle}>The strategic use of NMTC in promoting climate justice also addresses historical and systemic disparities. Many low-income communities, often predominantly inhabited by people of color, are located in areas with poor environmental conditions lacking access to green spaces and sustainable infrastructure.</p>
            
            <p style={simpleParagraphStyle}>By directing investments into these areas, NMTC helps rectify these inequalities, providing healthier living conditions and better economic opportunities.</p>
            
            <h2 style={headingStyle}>Leveraging Additional Incentives</h2>
            
            <p style={simpleParagraphStyle}>To maximize the impact of NMTC on climate justice, it is essential to leverage additional incentives and collaborate with other financial tools and programs. For example, pairing NMTC with Renewable Energy Tax Credits (RETC) or Historic Tax Credits (HTC) can amplify the benefits of investments, making projects more financially viable and impactful.</p>
            
            <p style={simpleParagraphStyle}>Sponsors and Owners should also track opportunities from the Inflation Reduction Act, including clean-energy tax credits and the deployment of the U.S. Environmental Protection Agency's recently announced Greenhouse Gas Reduction Fund (GGRF) awards earmarking billions of dollars in capital for building-decarbonization projects.</p>
            
            <h2 style={headingStyle}>Integrating Technology in Green Building</h2>
            
            <p style={simpleParagraphStyle}>Technological advancements are setting new standards in the construction industry, particularly in the realm of green building. The integration of technology in NMTC-funded green-building projects can further enhance their impact on climate justice and sustainability.</p>
            
            <p style={simpleParagraphStyle}>Here are some examples of how Sponsors and Owners can integrate Green Building principles using technology:</p>
            
            <div className="key-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', margin: '2rem 0' }}>
              <div className="stat" style={{ padding: '1.2rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={subheadingStyle}>Advanced Materials</h3>
                <p style={simpleParagraphStyle}>The use of recycled materials and low-VOC (volatile organic compound) paints help reduce the environmental impact.</p>
              </div>
              
              <div className="stat" style={{ padding: '1.2rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={subheadingStyle}>Energy Efficiency</h3>
                <p style={simpleParagraphStyle}>Integration of smart grids and renewable energy resources, like solar panels and wind turbines, into buildings is a key aspect of green technology.</p>
              </div>
              
              <div className="stat" style={{ padding: '1.2rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={subheadingStyle}>AI-Driven Systems</h3>
                <p style={simpleParagraphStyle}>AI-driven systems optimize HVAC and lighting systems within buildings, adjusting based on real-time data such as occupancy and weather conditions.</p>
              </div>
              
              <div className="stat" style={{ padding: '1.2rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3 style={subheadingStyle}>Predictive Maintenance</h3>
                <p style={simpleParagraphStyle}>AI and machine-learning algorithms are used to predict when building systems might fail or require maintenance, preventing downtime and reducing costs.</p>
              </div>
            </div>
            
            <h2 style={headingStyle}>Challenges and Opportunities</h2>
            
            <p style={simpleParagraphStyle}>The integration of technology in green building faces significant challenges stemming from increased costs of green design, a complex regulatory landscape that varies significantly across jurisdictions, and the pace of technological advancements outstripping existing regulations.</p>
            
            <h3 style={subheadingStyle}>Higher Upfront Costs</h3>
            
            <p style={simpleParagraphStyle}>Many sustainable building materials, energy-efficient systems and advanced technologies come with a higher upfront price tag. This can strain the already tight budgets of NMTC projects, which often operate with narrow financial margins to maximize their impact.</p>
            
            <p style={simpleParagraphStyle}>Conversely, technology can mitigate these cost challenges by enabling more accurate forecasting and tracking of project expenses. Advanced software tools and data analytics help project managers better estimate the costs associated with green-building technologies, allowing for more precise budgeting and financial planning.</p>
            
            <div className="article-sidebar" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', marginTop: '2rem' }}>
              <div className="article-authors" style={{ marginBottom: '1.5rem' }}>
                <h3 style={subheadingStyle}>Authors</h3>
                <div className="author">
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0.5rem 0' }}>Amir A.</h4>
                  <p style={simpleParagraphStyle}>Strategic Partner to CDFIs & Impact Investors | Founder, Clarity Impact Finance</p>
                </div>
              </div>
              
              <div className="article-topics" style={{ marginBottom: '1.5rem' }}>
                <h3 style={subheadingStyle}>Related Topics</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ display: 'inline-block', padding: '0.3rem 0.8rem', backgroundColor: '#e2f0e4', borderRadius: '20px', fontSize: '0.9rem' }}>Climate Justice</span>
                  <span style={{ display: 'inline-block', padding: '0.3rem 0.8rem', backgroundColor: '#e2f0e4', borderRadius: '20px', fontSize: '0.9rem' }}>NMTC</span>
                  <span style={{ display: 'inline-block', padding: '0.3rem 0.8rem', backgroundColor: '#e2f0e4', borderRadius: '20px', fontSize: '0.9rem' }}>Green Building</span>
                  <span style={{ display: 'inline-block', padding: '0.3rem 0.8rem', backgroundColor: '#e2f0e4', borderRadius: '20px', fontSize: '0.9rem' }}>Sustainable Development</span>
                </div>
              </div>
              
              <div className="article-contact">
                <h3 style={subheadingStyle}>Contact Us</h3>
                <Link to="/#contact" className="contact-btn" style={{ display: 'inline-block', padding: '0.8rem 1.5rem', backgroundColor: '#1B4620', color: 'white', textDecoration: 'none', borderRadius: '4px', fontWeight: '600', fontSize: '0.9rem' }}>Get in Touch</Link>
              </div>
            </div>
            
            <div className="back-to-home-bottom" style={{ marginTop: '3rem', marginBottom: '2rem' }}>
              <Link to="/" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', color: '#1B4620', fontWeight: '600' }}>
                <span className="back-arrow" style={{ marginRight: '0.5rem' }}>←</span> Back to Home
              </Link>
            </div>
            
            <div className="article-footer" style={{ backgroundColor: '#1B4620', color: 'white', padding: '2rem', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.4rem', marginBottom: '1rem', fontWeight: '600' }}>Ready to Incorporate Climate Justice Principles into Your NMTC Project?</h3>
              <p style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: '1.6' }}>At Clarity Impact Finance, we specialize in optimizing NMTC projects for both community impact and environmental sustainability. Whether you're exploring green building technologies or seeking to maximize the climate benefits of your development, our team can help.</p>
              <Link to="/#contact" className="primary-btn" style={{ display: 'inline-block', padding: '0.8rem 2rem', backgroundColor: 'white', color: '#1B4620', textDecoration: 'none', borderRadius: '4px', fontWeight: '600' }}>Contact Us Today</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default NmtcClimateJustice;